import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fetchRequestDetail, type PaymentRequestDetail } from "@/lib/payreq";
import { RequestForm } from "@/components/request-form";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Edit one of my requests. Only reachable when the detail page showed btnEdit, but the guard
 * is re-checked here (deep links/back navigation) and enforced server-side by apiSaveRequest.
 */
export default function EditRequest() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [req, setReq] = useState<PaymentRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token || !id) return;
    const controller = new AbortController();
    setLoading(true);
    fetchRequestDetail(token, id, controller.signal)
      .then((d) => {
        if (!d.btnEdit) {
          toast.error("This request can no longer be edited.");
          navigate(`/requests/${id}`, { replace: true });
          return;
        }
        setReq(d);
      })
      .catch((e) => {
        if ((e as Error | undefined)?.name === "AbortError") return;
        toast.error(e instanceof ApiError || e instanceof Error ? e.message : "Could not load the request.");
        navigate("/requests", { replace: true });
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [token, id, navigate]);

  if (loading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {Array.from({ length: 2 }).map((_, i) => (
          <Card size="sm" key={i}>
            <CardContent className="flex flex-col gap-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!req) return null;

  return <RequestForm mode="edit" initial={req} />;
}
